import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Calendar, ArrowRight } from 'lucide-react'
import { PageSeo, HeroSimple, Breadcrumbs, Button, NewsCard, NewsModal } from '@/components/ui'
import { LocalizedLink } from '@/components/LocalizedLink'
import { apiUrl } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { NewsItem } from '@/types/news'

export function BlogPage() {
  const { t, i18n } = useTranslation(['blog', 'common'])
  const [searchParams, setSearchParams] = useSearchParams()
  const activeSlug = searchParams.get('article')

  const [allNews, setAllNews] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterCategory, setFilterCategory] = useState<string>('all')

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await fetch(apiUrl(`/api/news?lang=${i18n.language}`))
        if (!res.ok) throw new Error('Failed to fetch news')
        const data = await res.json()
        setAllNews(data.news || [])
      } catch {
        setError(t('blog:errors.load'))
      } finally {
        setLoading(false)
      }
    }
    fetchNews()
  }, [i18n.language, t])

  const categories = useMemo(() => {
    const set = new Set(allNews.map((item) => item.category))
    return ['all', ...Array.from(set)]
  }, [allNews])

  const filteredNews = useMemo(() => {
    return allNews.filter((item) => {
      return filterCategory === 'all' || item.category === filterCategory
    })
  }, [allNews, filterCategory])

  const openArticle = (slug: string) => {
    setSearchParams({ article: slug })
  }

  const closeArticle = () => {
    searchParams.delete('article')
    setSearchParams(searchParams)
  }

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: t('blog:seo.title'),
        description: t('blog:seo.description'),
        url: typeof window !== 'undefined' ? window.location.href : '',
        blogPost: filteredNews.map((item) => ({
          '@type': 'BlogPosting',
          headline: item.title,
          description: item.excerpt,
          image: item.coverImage,
          datePublished: item.publishedAt,
          url: `${typeof window !== 'undefined' ? window.location.origin : ''}/blog?article=${item.slug}`,
        })),
      },
    ],
    [filteredNews, t],
  )

  return (
    <>
      <PageSeo
        title={t('blog:seo.title')}
        description={t('blog:seo.description')}
        type="website"
        jsonLd={jsonLd}
        breadcrumb={[
          { name: t('common:routes.home'), path: `/${i18n.language}` },
          { name: t('common:routes.blog') },
        ]}
      />

      <main className="min-h-screen bg-ink-deep">
        <HeroSimple
          title={t('blog:hero.title')}
          subtitle={t('blog:hero.subtitle')}
          variant="dark"
          watermark={
            <span className="font-display-italic text-[12rem] md:text-[18rem] leading-none text-primary/[0.06] select-none">
              AD
            </span>
          }
        />

        <Breadcrumbs
          items={[
            { label: t('common:routes.home'), to: 'home' },
            { label: t('common:routes.blog') },
          ]}
        />

        {/* Filters */}
        <section className="sticky top-0 z-30 bg-ink-deep/95 backdrop-blur-md border-y border-white/10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setFilterCategory(cat)}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium tracking-wide rounded-full border transition-colors',
                      filterCategory === cat
                        ? 'bg-primary text-ink border-primary'
                        : 'bg-transparent text-white/70 border-white/15 hover:border-primary/50 hover:text-white'
                    )}
                  >
                    {cat === 'all' ? t('blog:filters.all') : cat}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-4">
                <span className="font-display-italic text-2xl text-primary tabular-nums">
                  {String(filteredNews.length).padStart(2, '0')}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Archive grid */}
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Calendar className="w-5 h-5 text-primary animate-pulse mr-2" />
                <span className="text-white/60">{t('common:aria.loading')}…</span>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-white/70">{error}</p>
              </div>
            ) : filteredNews.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-white/70">{t('blog:errors.empty')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {filteredNews.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                  >
                    <NewsCard
                      index={index}
                      title={item.title}
                      excerpt={item.excerpt}
                      date={item.publishedAt}
                      category={item.category}
                      image={item.coverImage}
                      onOpen={() => openArticle(item.slug)}
                    />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-primary py-16 md:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-h2 font-semibold text-ink">
              {t('blog:cta.title')}
            </h2>
            <p className="mt-4 text-lead text-ink/80 max-w-2xl mx-auto">
              {t('blog:cta.subtitle')}
            </p>
            <div className="mt-8">
              <LocalizedLink to="bookConsultation">
                <Button variant="primary-light">
                  {t('blog:cta.button')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </LocalizedLink>
            </div>
          </div>
        </section>
      </main>

      <NewsModal slug={activeSlug} onClose={closeArticle} />
    </>
  )
}
